import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Maximize, Check, X, Filter, Search, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { spaces } from '../data/mock';

const SpacesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);

  const spaceTypes = ['all', ...new Set(spaces.map(s => s.type))];

  const filteredSpaces = spaces.filter(space => {
    const matchesSearch = space.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          space.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || space.type === filterType;
    const matchesAvailability = !showOnlyAvailable || space.available;
    
    return matchesSearch && matchesType && matchesAvailability;
  });

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-slate-900">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1527192491265-7e15c55b1ed2?w=1600&q=80"
            alt="Coworking Space"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Locales <span className="text-sky-400">Disponibles</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Explora nuestra selección de espacios corporativos y encuentra 
              el lugar perfecto para tu equipo de trabajo.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white py-8 border-b sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o ubicación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-72 border-slate-200"
                />
              </div>

              {/* Type Filter */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-48 border-slate-200">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Tipo de espacio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {spaceTypes.filter(t => t !== 'all').map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Availability Toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                  showOnlyAvailable
                    ? 'bg-sky-50 border-sky-500 text-sky-600'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Check className={`w-4 h-4 ${showOnlyAvailable ? 'opacity-100' : 'opacity-0'}`} />
                <span className="text-sm font-medium">Solo disponibles</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Spaces Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Count */}
          <div className="mb-8">
            <p className="text-slate-600">
              Mostrando <span className="font-semibold text-slate-800">{filteredSpaces.length}</span> espacios
            </p>
          </div>

          {filteredSpaces.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredSpaces.map((space) => (
                <Card
                  key={space.id}
                  className={`group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 ${
                    !space.available ? 'opacity-75' : ''
                  }`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={space.image}
                      alt={space.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <Badge className="bg-sky-500 hover:bg-sky-500 text-white">
                        {space.type}
                      </Badge>
                      {space.available ? (
                        <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">
                          Disponible
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-slate-500 text-white">
                          No disponible
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">
                      {space.name}
                    </h3>
                    
                    <div className="flex items-center text-slate-600 text-sm mb-4">
                      <MapPin className="w-4 h-4 mr-1 text-sky-500" />
                      {space.location}
                    </div>

                    <div className="flex items-center gap-4 mb-4 text-sm text-slate-600">
                      <div className="flex items-center">
                        <Maximize className="w-4 h-4 mr-1" />
                        {space.size}
                      </div>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {space.capacity}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {space.features.slice(0, 3).map((feature, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
                        >
                          {feature}
                        </span>
                      ))}
                      {space.features.length > 3 && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-md">
                          +{space.features.length - 3} más
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <p className="text-2xl font-bold text-sky-600">{space.price}</p>
                      </div>
                      <Link to="/contacto">
                        <Button
                          disabled={!space.available}
                          className={space.available 
                            ? "bg-sky-500 hover:bg-sky-600 text-white" 
                            : "bg-slate-300 text-slate-500 cursor-not-allowed"
                          }
                        >
                          {space.available ? 'Solicitar' : 'No disponible'}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">
                No se encontraron espacios
              </h3>
              <p className="text-slate-600 mb-6">
                Intenta ajustar los filtros de búsqueda.
              </p>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                  setShowOnlyAvailable(false);
                }}
                variant="outline"
                className="border-sky-500 text-sky-600"
              >
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            ¿No encuentras lo que buscas?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Contáctanos y te ayudaremos a encontrar el espacio perfecto para tu empresa.
          </p>
          <Link to="/contacto">
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-6 text-lg">
              Hablar con un Asesor
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SpacesPage;
